import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useSite } from "@/i18n/SiteContext";
import { LanguageSwitcher } from "@/components/site/LanguageSwitcher";
import { LANG_LABELS } from "@/i18n/strings";
import { Logo } from "@/components/site/Logo";

export { Logo };

const HERO_ROUTES = ["/", "/about", "/tours", "/contact", "/packages", "/diary"];

export const Header = () => {
  const { t, lang, setLang } = useSite();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const overHero = HERO_ROUTES.includes(pathname) || pathname.startsWith("/tours/") || pathname.startsWith("/packages/") || pathname.startsWith("/diary/");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const solid = scrolled || !overHero || open;
  const light = !solid;
  const links = [
    { to: "/tours", label: t("nav.tours"), id: "tours" },
    { to: "/packages", label: t("nav.packages"), id: "packages" },
    { to: "/diary", label: t("nav.diary"), id: "diary" },
    { to: "/about", label: t("nav.about"), id: "about" },
    { to: "/contact", label: t("nav.contact"), id: "contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 ${solid ? "bg-ivory/95 backdrop-blur-md border-b border-sand shadow-[0_4px_20px_rgba(15,46,62,0.05)]" : "bg-transparent border-b border-transparent"}`}
      style={{ transition: "background-color .35s ease, border-color .35s ease" }}
    >
      <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
        <Link to="/" data-testid="header-logo" aria-label="AMS Travel home"><Logo light={light} /></Link>
        <nav className="hidden md:flex items-center gap-7">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.id}`}
              className={({ isActive }) =>
                `relative text-[11px] font-medium tracking-[0.2em] uppercase py-1 after:absolute after:left-0 after:-bottom-0.5 after:h-px after:bg-current after:origin-left after:transition-transform after:duration-300 ${isActive ? "after:w-full after:scale-x-100" : "after:w-full after:scale-x-0 hover:after:scale-x-100"} ${light ? "text-ivory" : "text-ocean"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <LanguageSwitcher light={light} />
          <Link
            to="/tours"
            data-testid="header-book-cta"
            className={`px-6 py-3 rounded-full text-[11px] font-medium tracking-[0.2em] uppercase btn-lift ${light ? "bg-ivory text-ocean hover:bg-sand-brand" : "bg-ocean text-ivory hover:bg-ocean-700"}`}
          >
            {t("nav.book")}
          </Link>
        </nav>
        <button className={`md:hidden p-2 ${light ? "text-ivory" : "text-ocean"}`} onClick={() => setOpen((o) => !o)} aria-label="Menu" data-testid="mobile-menu-toggle">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      {open && (
        <div className="md:hidden bg-ivory border-t border-sand px-6 py-6 space-y-5" data-testid="mobile-menu">
          {links.map((l) => (
            <Link key={l.to} to={l.to} className="block text-sm tracking-[0.18em] uppercase text-ocean" data-testid={`mobile-nav-${l.id}`}>{l.label}</Link>
          ))}
          <div className="flex gap-2 pt-2">
            {Object.keys(LANG_LABELS).map((c) => (
              <button key={c} onClick={() => setLang(c)} data-testid={`mobile-lang-${c}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border ${lang === c ? "bg-ocean text-ivory border-ocean" : "border-ocean/20 text-ocean"}`}>
                {c.toUpperCase()}
              </button>
            ))}
          </div>
          <Link to="/tours" className="block text-center px-5 py-3.5 bg-ocean text-ivory text-xs tracking-[0.18em] uppercase rounded-full" data-testid="mobile-book-cta">{t("nav.book")}</Link>
        </div>
      )}
    </header>
  );
};
